import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { DateTime } from 'luxon';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { trackById } from '../../../../utils/track-by';
import { Alert, alertMCode } from 'src/app/models/Alert';
import { AlertService } from 'src/app/services/alert.service';
import { NotificationsInboxService } from 'src/app/services/notifications-inbox.service';
import { formatAlertAudience, alertAudienceTone } from 'src/app/utils/alert-audience';
import { alertDeepLinkPath } from 'src/app/utils/alert-deep-link';
import { formatAlertEventType, alertEventTypeTone, isEnrollmentDuplicatesAlert } from 'src/app/utils/alert-event-type';
import { Notification } from '../interfaces/notification.interface';

@Component({
  selector: 'vex-toolbar-notifications-dropdown',
  templateUrl: './toolbar-notifications-dropdown.component.html',
  styleUrls: ['./toolbar-notifications-dropdown.component.scss']
})
export class ToolbarNotificationsDropdownComponent implements OnInit {
  notifications: Notification[] = [];
  loading = false;
  trackById = trackById;

  constructor(
    private alertService: AlertService,
    private notificationsInbox: NotificationsInboxService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.notificationsInbox.markSeen();
    this.loadNotifications();
  }

  loadNotifications(): void {
    this.loading = true;
    this.alertService.list({ page: 1, pageSize: 10 }).pipe(
      map(result => (result?.items ?? [])
        .filter(alert => !isEnrollmentDuplicatesAlert(alert.eventType ?? alert.category))
        .map(alert => this.mapAlert(alert))),
      catchError(() => of([] as Notification[]))
    ).subscribe({
      next: (alerts) => {
        this.notifications = alerts;
        this.loading = false;
      },
      error: () => {
        this.notifications = [];
        this.loading = false;
      }
    });
  }

  openNotification(notification: Notification): void {
    if (notification.route) {
      this.router.navigateByUrl(notification.route);
    }
  }

  viewAll(): void {
    this.router.navigate(['/notifications']);
  }

  private mapAlert(alert: Alert): Notification {
    const mcode = alertMCode(alert);
    const client = alert.clientName && mcode
      ? `${alert.clientName} (${mcode})`
      : (alert.clientName || mcode || 'Client');
    const eventType = formatAlertEventType(alert.eventType ?? alert.category);
    const audience = formatAlertAudience(alert.audience);

    return {
      id: alert.id,
      label: alert.subject || client,
      description: client,
      eventType: eventType || 'Alert',
      eventTypeTone: alertEventTypeTone(alert.eventType ?? alert.category),
      audience: audience || undefined,
      audienceTone: alertAudienceTone(alert.audience),
      icon: 'mat:notifications_active',
      colorClass: 'text-primary',
      datetime: DateTime.fromISO(alert.createdAt),
      route: alertDeepLinkPath(alert) || '/notifications',
      read: false
    };
  }
}
